// TODO: Implement better live-asr-messages

import { StateUpdater } from "preact/hooks";
import { ClientAttributes } from "./getClientAttributes";
import { Transcript } from "./Transcript";
import { CaptionFrame } from "./types";

type CaptionFetcherConfig = Pick<
  ClientAttributes,
  "useUppercase" | "pollAfterEvent"
> & {
  captionsKey?: string;
  delaySeconds?: number;
  insertParagraphs: boolean;
  setMessage: StateUpdater<string>;
  setTranscript: StateUpdater<string>;
};

type FetchType = "full_history" | "latest";

export class CaptionFetcher {
  /**
   * The live event's key for fetching properly out of S3
   */
  public captionsKey: CaptionFetcherConfig["captionsKey"];

  /**
   * The polling time to re-fetch _full_history in the event of an error in the request
   */
  private ERROR_POLLING_INTERVAL = 5000;

  /**
   * Whether or not the transcript should include character count based paragraphs
   */
  private insertParagraphs: CaptionFetcherConfig["insertParagraphs"];

  /**
   * The last packet number received and displayed
   *
   * This is used to refetch full_history when a packet number is missed
   */
  private lastPacketNumberReceived = 0;

  /**
   * The polling time to re-fetch _latest while the event is running
   */
  private LATEST_POLLING_INTERVAL = 1000;

  /**
   * A callback to set the live-asr-message content
   */
  private setMessage: CaptionFetcherConfig["setMessage"];

  /**
   * A callback to set the live-asr-target content
   */
  private setTranscript: CaptionFetcherConfig["setMessage"];

  /**
   * The polling time to re-fetch _latest while the event is running
   */
  private STATIC_POLLING_INTERVAL = 10000;

  /**
   * Whether or not the embed screen is on a static url
   */
  private pollAfterEvent: boolean;

  /**
   * A class that creates full transcripts from frames
   */
  private transcript: Transcript | undefined;

  /**
   * Whether or not the captions should be displayed in uppercase
   */
  private useUppercase: CaptionFetcherConfig["useUppercase"];

  /**
   * Number of seconds to delay displaying captions after they are produced.
   */
  private delaySeconds: CaptionFetcherConfig["delaySeconds"];

  /**
   * The ID of the current polling timeout
   */
  private pollingTimeoutId: number | undefined;

  /**
   * A single AbortController to handle race conditions
   */
  private abortController: AbortController = new AbortController();

  constructor({
    insertParagraphs,
    captionsKey,
    delaySeconds,
    setMessage,
    setTranscript,
    pollAfterEvent,
    useUppercase,
  }: CaptionFetcherConfig) {
    this.insertParagraphs = insertParagraphs;
    this.captionsKey = captionsKey;
    this.delaySeconds = delaySeconds;
    this.pollAfterEvent = pollAfterEvent;
    this.setMessage = setMessage;
    this.setTranscript = setTranscript;
    this.useUppercase = useUppercase;
  }

  /**
   * Start fetching captions. Allows for updating the captions key.
   */
  async start(captionsKey?: string) {
    // Protect against race conditions
    this.abortInFlightRequests();
    this.clearTimeouts();
    this.clearTranscript();

    if (captionsKey !== undefined) {
      this.captionsKey = captionsKey;
    }

    // Reset the last packet number
    this.lastPacketNumberReceived = 0;

    // Create a new transcript
    this.transcript = new Transcript({
      insertParagraphs: this.insertParagraphs,
      useUppercase: this.useUppercase,
      delaySeconds: this.delaySeconds,
    });

    // Fetch the full history
    this.fetchFrames("full_history");
  }

  /**
   * Fetch frames from S3 and handle appropriately
   */
  private async fetchFrames(type: FetchType) {
    /**
     * The fetch response
     */
    const response = await fetch(
      `https://originp3.s3.amazonaws.com/live-asr/${this.captionsKey}_${type}.json`,
      {
        method: "GET",
        signal: this.abortController.signal,
      }
    );

    // If the request fails, set a message for the user and try again in ERROR_POLLING_INTERVAL
    if (!response.ok) {
      this.setMessage("Captioning for this event has not started");
      setTimeout(() => this.start(), this.ERROR_POLLING_INTERVAL);
      return;
    }

    const frames: CaptionFrame[] = await response.json();

    // If the first frame received is greater than one more than the last packet
    // number received OR the first frame received is well behind (using 10 for now)
    // the last packet number we received, then we want to "re-start" (ie: refetch
    // full history) and then start polling latest again, so we don't miss any frames
    // OR so we can display the start of a new event.
    if (
      frames[0]?.packet_number > this.lastPacketNumberReceived + 1 ||
      frames[0]?.packet_number < this.lastPacketNumberReceived - 10
    ) {
      this.start();
      return;
    }

    // Set the last received packet number
    if (frames.slice(-1)[0]?.packet_number)
      this.lastPacketNumberReceived = frames.slice(-1)[0]?.packet_number;

    // Handle the new frames
    this.handleFrames(frames);
  }

  /**
   * Trigger appropriate App callbacks based off the new frames and set a new
   * polling interval
   */
  private handleFrames(frames: CaptionFrame[]) {
    // Add frames to the transcript
    this.transcript?.addFrames(frames);

    /**
     * The displayed transcript content
     */
    const transcriptContent = this.transcript?.getHtml() || "";

    // Set the transcript content
    this.setTranscript(transcriptContent);

    // If there is no content and no frames have been ingested at all,
    // let the user know the event has not started. If frames exist but
    // are being held back by the delay, show no message.
    if (transcriptContent.length === 0 && !this.transcript?.hasFrames()) {
      this.setMessage("Captioning for this event has not started");
    } else {
      this.setMessage("");
    }

    this.triggerPolling(frames);
  }

  /**
   * Triggers the correct polling behavior based off the status of the event
   */
  private triggerPolling(frames: CaptionFrame[]) {
    /**
     * Whether or not the event is complete
     */
    const eventComplete = frames.slice(-1)[0]?.complete;

    // If the event is not complete, start polling for the latest frames
    if (!eventComplete) {
      this.pollingTimeoutId = setTimeout(
        () => this.fetchFrames("latest"),
        this.LATEST_POLLING_INTERVAL
      ) as unknown as number;
      return;
    }

    // If the event is complete and the embed screen is a static url, then
    // start polling for the full history of the potential next event
    if (this.pollAfterEvent) {
      this.pollingTimeoutId = setTimeout(
        () => this.fetchFrames("full_history"),
        this.STATIC_POLLING_INTERVAL
      ) as unknown as number;
    }

    // Set the event complete message
    this.setMessage("Event complete");
  }

  private abortInFlightRequests() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }
  }

  private clearTimeouts() {
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
      this.pollingTimeoutId = undefined;
    }
  }

  private clearTranscript() {
    this.setTranscript("");
    this.transcript = undefined;
  }
}
